/**
 * l1aSteps — PURE derivation of the L1A 5-step flow states (Stage 1R: per-property
 * evaluation restructure).
 *
 * OWNER CORRECTION (2026-06-27): the flow is PER-PROPERTY (vertical), not
 * per-dimension (horizontal). You pick a property and do its WHOLE evaluation
 * (grid → entrance → AUO) to completion, then the next property — NOT grid-for-all,
 * then entrance-for-all. So the old global dimension-steps (Grid · Entrances · AUO)
 * COLLAPSE into ONE "Per-property evaluation" step that is satisfied PER candidate.
 *
 * Five steps now:
 *   Properties · Per-property evaluation · Comparative · Verdict · Report.
 *
 * The L1AFlow orchestrator owns NO parallel data store: every step state is
 * DERIVED from the SAME reads the panels already do — the loaded candidate
 * properties (answers.properties[] → props_), each candidate's grid status
 * (candidateGrid predicates), the marks on their floors (doors/fixtures), the
 * recommended pick, and the latest report status. This module is that pure
 * derivation, fully unit-tested, no Firestore / no React.
 *
 * Each step gets a state ∈ locked | blocked | ready | in_progress | done and a
 * one-line status string (the collapsed status / the blocked-reason copy).
 */
import {
  type GriddableProperty,
  type GriddableFloor,
  floorHasBoundary,
  floorHasEntrance,
  floorAuoResolved,
  gridSummary,
} from './candidateGrid';

export type L1AStepState = 'locked' | 'blocked' | 'ready' | 'in_progress' | 'done';
export type L1AStepKey =
  | 'property'
  | 'per_property_eval'
  | 'comparative'
  | 'verdict'
  | 'report';

export interface L1AStepView {
  key: L1AStepKey;
  n: number;
  title: string;
  state: L1AStepState;
  /** One-line collapsed status / blocked-reason copy. */
  status: string;
}

/** A candidate carries the registry FK on top of the griddable shape. */
export interface L1ACandidate extends GriddableProperty {
  propertyId?: string;
}

export interface L1AStepCtx {
  /** loaded answers.properties[] (props_). null = still loading / not fetched. */
  candidates: L1ACandidate[] | null;
  /** projects.propertyRefs[] — registry links (for the "linked-not-joined" hint). */
  propertyRefs?: Array<{ propertyId: string }>;
  /** the recommended candidate's slot id (answers.recommended_property_id). */
  recommendedId?: string;
  /** latest report status for this tier, or null when no report exists. */
  reportStatus?: 'draft' | 'approved' | 'delivered' | null;
  /** true while candidates are genuinely in flight (no client / fetch pending). */
  loading?: boolean;
}

const TITLES: Record<L1AStepKey, string> = {
  property: 'Properties',
  per_property_eval: 'Per-property evaluation',
  comparative: 'Comparative',
  verdict: 'Verdict',
  report: 'Report',
};

/** floors of a candidate, normalised. */
function floorsOf(c: L1ACandidate | null | undefined): GriddableFloor[] {
  return Array.isArray(c?.floors) ? (c!.floors as GriddableFloor[]) : [];
}
function pl(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`;
}

// ── Per-candidate evaluation predicates (the COLLAPSED grid+entrance+AUO) ──────
/** EVALUATED = boundary traced AND ≥1 entrance (AD-048: AUO optional to proceed). */
export function candidateEvaluated(c: L1ACandidate | null | undefined): boolean {
  const fls = floorsOf(c);
  return fls.some(floorHasBoundary) && fls.some(floorHasEntrance);
}
/** COMPLETE = evaluated AND every floor's AUO marks resolved (none 'proposed'). */
export function candidateComplete(c: L1ACandidate | null | undefined): boolean {
  return candidateEvaluated(c) && floorsOf(c).every(floorAuoResolved);
}

/**
 * Derive the five L1A step states from the shared reads. Pure + total — any
 * partial/empty ctx yields a coherent set (the first incomplete step is the one
 * the orchestrator opens by default).
 */
export function deriveL1AStepStates(ctx: L1AStepCtx): L1AStepView[] {
  const loading = !!ctx.loading || ctx.candidates == null;
  const cands = Array.isArray(ctx.candidates) ? ctx.candidates : [];
  const refs = Array.isArray(ctx.propertyRefs) ? ctx.propertyRefs : [];
  const recommendedId = ctx.recommendedId || '';
  const reportStatus = ctx.reportStatus ?? null;

  const joined = cands.filter((c) => !!c.propertyId);
  const summary = gridSummary(cands, recommendedId);
  // Back-compat: the winner now keys on stable `propertyId` (`P#####`), but a
  // legacy value may still be the candidate slot `id` (`prop_…`) — resolve either.
  const recCand = cands.find(
    (c) => recommendedId === c.propertyId || recommendedId === c.id,
  );
  const recLabel = recCand?.propertyId || recCand?.label || '';

  // ── Step 1 — Properties ──────────────────────────────────────────────────
  let s1: L1AStepState;
  let s1status: string;
  if (loading) {
    s1 = 'ready';
    s1status = 'Loading candidate properties…';
  } else if (cands.length === 0) {
    s1 = 'blocked';
    s1status = 'Add a candidate property and link it to the registry to begin.';
  } else if (joined.length === 0) {
    s1 = 'in_progress';
    s1status =
      refs.length > 0
        ? `${pl(cands.length, 'candidate')} · linking to the registry…`
        : `${pl(cands.length, 'candidate')} · link each to a registry property to continue`;
  } else {
    s1 = 'done';
    s1status =
      `${pl(joined.length, 'candidate')} joined` +
      (recLabel ? ` · ${recLabel} recommended` : '');
  }
  const step1Done = s1 === 'done';

  // ── Step 2 — Per-property evaluation (grid + entrance + AUO, per candidate) ─
  // The owner's correction: evaluate ONE property fully, then the next. A
  // candidate is EVALUATED once its boundary is traced AND it has ≥1 entrance
  // (AUO optional to proceed, AD-048); COMPLETE adds all-AUO-resolved. The step
  // is DONE only when EVERY joined candidate is evaluated — one property's
  // entrance can no longer flip the whole project green.
  const evaluatedCount = joined.filter(candidateEvaluated).length;
  const completeCount = joined.filter(candidateComplete).length;
  let s2: L1AStepState;
  let s2status: string;
  if (!step1Done) {
    s2 = 'locked';
    s2status = 'Link a candidate property first.';
  } else if (evaluatedCount === joined.length) {
    s2 = 'done';
    s2status =
      `${pl(joined.length, 'property', 'properties')} evaluated` +
      (completeCount === joined.length ? ' · all AUO confirmed' : '');
  } else {
    s2 = 'in_progress';
    s2status = `${evaluatedCount} of ${joined.length} properties evaluated`;
  }
  const perPropEvalDone = s2 === 'done';

  // ── Step 3 — Comparative (the cross-property §C compare) ──────────────────
  // Locked until per-property evaluation is done. A REAL comparison wants ≥2
  // evaluated; a single-property engagement is NOT hard-blocked — comparative is
  // trivially done/skipped. Body = Stage 2 (placeholder for now).
  let s3: L1AStepState;
  let s3status: string;
  if (!perPropEvalDone) {
    s3 = 'locked';
    s3status =
      joined.length <= 1
        ? 'Finish evaluating the property first.'
        : `Evaluate every property first — ${evaluatedCount} of ${joined.length} done.`;
  } else if (joined.length <= 1) {
    s3 = 'done';
    s3status = 'Single property — no cross-property comparison needed.';
  } else {
    s3 = 'ready';
    const pendingNote =
      summary.pendingIds.length > 0
        ? ` · ${summary.pendingIds.length} pending left out`
        : '';
    s3status = `Compares the ${pl(summary.proceedable, 'evaluated property', 'evaluated properties')}${pendingNote}`;
  }
  const comparativeReachable = perPropEvalDone;

  // ── Step 4 — Verdict (recommended property + statement + reasoning) ───────
  // Locked until comparative is reachable. DONE when a recommended property is
  // set AND it resolves to a proceed-able candidate (ADR-7). The AI-drafted
  // statement is Stage 3; here we gate on the pick being valid.
  const verdictValid = summary.recommendedValid;
  let s4: L1AStepState;
  let s4status: string;
  if (!comparativeReachable) {
    s4 = 'locked';
    s4status = 'Complete the evaluation first.';
  } else if (verdictValid) {
    s4 = 'done';
    s4status = recLabel ? `${recLabel} recommended` : 'Recommendation confirmed';
  } else if (!summary.readyToGenerate) {
    s4 = 'blocked';
    s4status = 'Grid at least one property (boundary + an entrance) first.';
  } else {
    s4 = 'ready';
    s4status = summary.isComparison
      ? `Confirm the recommended property among the ${summary.proceedable} evaluated.`
      : 'Confirm the recommended property + write the verdict.';
  }

  // ── Step 5 — Report (generate → approve → deliver) ───────────────────────
  // Locked until a valid verdict. Once unlocked, follows the report status; the
  // ready-copy names any pending (not-yet-proceed-able) candidates excluded.
  const pendingCount = summary.pendingIds.length;
  const pendingNote = pendingCount > 0 ? ` · ${pendingCount} pending left out` : '';
  let s5: L1AStepState;
  let s5status: string;
  if (!verdictValid) {
    s5 = 'locked';
    s5status = 'Confirm the verdict (recommended property) first.';
  } else if (reportStatus === 'delivered') {
    s5 = 'done';
    s5status = 'Delivered';
  } else if (reportStatus === 'draft' || reportStatus === 'approved') {
    s5 = 'in_progress';
    s5status =
      reportStatus === 'approved'
        ? 'Approved — ready to deliver'
        : 'Draft generated — review & deliver';
  } else {
    s5 = 'ready';
    s5status = summary.isComparison
      ? `Ready — Generate compares the ${summary.proceedable} ready properties${pendingNote}`
      : `Ready — Generate the report${pendingNote}`;
  }

  return [
    { key: 'property', n: 1, title: TITLES.property, state: s1, status: s1status },
    { key: 'per_property_eval', n: 2, title: TITLES.per_property_eval, state: s2, status: s2status },
    { key: 'comparative', n: 3, title: TITLES.comparative, state: s3, status: s3status },
    { key: 'verdict', n: 4, title: TITLES.verdict, state: s4, status: s4status },
    { key: 'report', n: 5, title: TITLES.report, state: s5, status: s5status },
  ];
}

/** The step the flow opens by default = the first NON-done, NON-locked step
 *  (falls back to the first non-done, else the last step). */
export function firstOpenStep(views: L1AStepView[]): L1AStepKey {
  const actionable = views.find((v) => v.state !== 'done' && v.state !== 'locked');
  if (actionable) return actionable.key;
  const notDone = views.find((v) => v.state !== 'done');
  if (notDone) return notDone.key;
  return views[views.length - 1]?.key ?? 'property';
}

/** A step is clickable (expandable) when it is not locked. */
export function stepClickable(state: L1AStepState): boolean {
  return state !== 'locked';
}
